import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  useClose,
} from '@headlessui/react'
import { ChildrenProp } from '../types_and_interfaces.ts'
import { cn } from '../utilities.ts'
import { ElementType } from 'react'
import { useTranslation } from 'react-i18next'

export type AppDialogProps = {
  open: boolean
  setOpen: (open: boolean) => void
  panelClassName?: string
  position?: string
}

export default function AppDialog({
  open,
  setOpen,
  children,
  panelClassName,
  position = 'center',
  ...props
}: AppDialogProps & ChildrenProp) {
  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      className="relative z-[100]"
      {...props}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div
          className={`flex min-h-full items-${position} justify-center text-center sm:items-center`}
        >
          <DialogPanel
            transition
            className={cn(
              'relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-sm sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 ',
              panelClassName
            )}
          >
            {children}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}

const Content = () => {
  const { t: translate } = useTranslation()
  const close = useClose()
  return (
    <>
      <div>
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
          <i className="ki-filled ki-check"></i>
        </div>
        <div className="mt-3 text-center sm:mt-5">
          <DialogTitle
            as="h3"
            className="text-base font-semibold leading-6 text-gray-900"
          >
            {translate('payment_successful')}
          </DialogTitle>
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              {translate('lorem_ipsum_dolor')}
            </p>
          </div>
        </div>
      </div>
      <div className="mt-5 sm:mt-6">
        <button
          type="button"
          onClick={() => close()}
          className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
        >
          {translate('go_back_to_dashboard')}
        </button>
      </div>
    </>
  )
}

AppDialog.Content = Content

const Title = ({
  as = 'h3',
  className,
  children,
}: ChildrenProp & { as?: ElementType; className?: string }) => {
  return (
    <DialogTitle
      as={as}
      className={cn(
        className,
        'text-base font-semibold leading-6 text-gray-900'
      )}
    >
      {children}
    </DialogTitle>
  )
}

AppDialog.Title = Title
